import React, { useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useSelector } from 'react-redux';
import { Field, FieldArray, FieldArrayRenderProps, FieldProps, Form, Formik, FormikErrors, FormikTouched } from 'formik';
import orderBy from 'lodash/orderBy';
import { v4 as uuid } from 'uuid';
import * as Yup from 'yup';
import { AlertContent, CardRadio, InputIcons, InputIconWrapper, ModalFactory, Select } from 'components';
import { ISelectOption } from 'components/Select/types';
import { appConfig } from 'config/app';
import { selectJobTitlesBySite, selectPOSCategories, selectPOSEmployees } from 'store/Collections';
import { IJobTitleState } from 'store/types';
import {
  calculatorParticipants,
  CalculatorParticipantType,
  CalculatorRuleCondition,
  CalculatorSplitMethod,
  HOUSE_ACCOUNT_NAME,
  ICalculatorParticipant,
  ICategoryGroup,
  IFormBeneficiary,
  IFormCalculatorRule,
  IFormCalculatorRuleShare,
  IFormRemainderBeneficiary,
  IFormValues,
  IPOSCategory,
  IPOSLaborEmployee,
  ITipShareFormValues,
  RuleFundSource,
  ShareEligibilityMethod,
  ShareRemainderType,
  ShareRuleType,
} from 'types';
import { displayContributorType, toTitleCase } from 'utils';
import { SectionTitle } from '../../../../../Settings/components';
import { AddButton, StyledIconButton } from '../../../../styles';
import {
  ButtonContainer,
  Column,
  ContributingCardsRow,
  ErrorText,
  FieldContainer,
  JobTitleField,
  ModalContent,
  ModalInfo,
  NumberInput,
  StyledButton,
  StyledButtonText,
  StyledCheckbox,
  StyledDiv,
  StyledRow,
  TipCalculatorFieldsContainer,
  TipCalculatorRow,
  TipShareFormContainer,
  TipShareRow,
  TipSharesText,
} from '../../../shared/styles';
import { checkCategoryMatch, onPointsChange, roundNumber, validatePercentage, validateShareJob } from '../../../shared/utils';

const jobRuleText = 'Job rules specify how employees working a specific job share or pool tips. For example Servers sharing with Bartenders.';
const ownerRuleText = 'Owner rules are tied to a specific check owner, not a job, and take priority over any job rule. Use them for individual employees, Online Order Systems, or shared bar terminals.';

const checkVirtualRuleConflict = (values: ITipShareFormValues, currentJob: string) => values.isVirtual && values.contributor?.name === currentJob;

const remainderSelectorOptions = [
  { value: ShareRemainderType.SELF, label: 'Self' },
  { value: ShareRemainderType.POOL, label: 'Pooled' },
];

const defaultJob = { name: '', type: CalculatorParticipantType.JOB };
const defaultShare = { ...defaultJob, split: CalculatorSplitMethod.TIME_WORKED };
const defaultRemainder = (id: string) => ({
  id,
  participants: [{ ...defaultJob, id }],
  source: `${RuleFundSource.AUTO_GRATUITIES}|${RuleFundSource.TIPS}`,
  split: CalculatorSplitMethod.TIME_WORKED,
});

const sections = [
  { title: 'Source', type: ShareRuleType.TIP },
  { title: 'Sales Groups', type: ShareRuleType.CATEGORY_GROUP },
  { title: 'Sales Categories', type: ShareRuleType.CATEGORY },
];

const sourceOptions = [
  { value: { category: RuleFundSource.AUTO_GRATUITIES, type: ShareRuleType.TIP }, label: 'Autograts' },
  { value: { category: RuleFundSource.TIPS, type: ShareRuleType.TIP }, label: 'Tips' },
  { value: { category: `${RuleFundSource.AUTO_GRATUITIES}|${RuleFundSource.TIPS}`, type: ShareRuleType.TIP }, label: 'Tips & Autograts' },
];

const mapCategories = (categories: Array<IPOSCategory | ICategoryGroup>, type: ShareRuleType) =>
  orderBy(
    categories.map((cat) => ({ value: { category: cat.name, type }, label: toTitleCase(cat.name) })),
    [(cat) => cat.label.toLowerCase()],
    ['asc']
  );

const mapEmployees = (employees: IPOSLaborEmployee[], rules: IFormCalculatorRule[], selectedRule?: IFormCalculatorRule) => {
  const ownerRules = rules.filter((r) => r.sharePolicy && r.id !== selectedRule?.id && r.contributor?.type === CalculatorParticipantType.EMPLOYEE);
  return orderBy(
    employees.reduce((acc, emp) => {
      // filter out employees that already contribute to some tip share
      if (!ownerRules.some((r) => r.contributor.providerId === emp.id)) acc.push({ value: emp.id, label: toTitleCase(emp.name) });
      return acc;
    }, []),
    [(emp) => emp.label.toLowerCase()],
    ['asc']
  );
};

const eligibilityMethodOptions = [
  {
    icon: 'Receipt',
    size: 18,
    value: ShareEligibilityMethod.BY_TICKET,
    tooltip: (
      <>
        Share on a check
        <br />
        by check basis
      </>
    ),
  },
  {
    icon: 'CalendarMinimal',
    value: ShareEligibilityMethod.BY_SEGMENT,
    tooltip: (
      <>
        Share on all checks
        <br />
        in the segment
      </>
    ),
  },
];

const jobSplitOptions = [
  {
    icon: 'Clock',
    value: CalculatorSplitMethod.TIME_WORKED,
    tooltip: (
      <>
        Divide pool using time worked
        <br />
        by each participant
      </>
    ),
  },
  {
    icon: 'SplitEven',
    value: CalculatorSplitMethod.EVENLY,
    tooltip: (
      <>
        Divide pool evenly
        <br />
        among all participants
      </>
    ),
  },
];

interface ICreateTipShare {
  hideModal: () => void;
  initialValues?: IFormValues;
  onConfirm: (values: IFormValues, onSuccess?: () => void) => void;
  siteId: string;
  isLoading?: boolean;
  selectedRule?: IFormCalculatorRule;
}

export const CreateTipShare = ({ hideModal, initialValues, onConfirm, siteId, isLoading, selectedRule }: ICreateTipShare) => {
  const [contributingJobTitle, setContributingJobTitle] = useState<string>('');
  const posCategories = useSelector(selectPOSCategories());
  const posEmployees = useSelector(selectPOSEmployees());
  const jobTitles = useSelector(selectJobTitlesBySite(siteId));
  const [jobTitlesList, setJobTitlesList] = useState<IJobTitleState[]>(jobTitles);

  const onNewJobTitle = (newJob: IJobTitleState) => {
    setJobTitlesList((prev = []) => [...prev, newJob]);
  };

  const participantsSelectorOptions = useMemo(() => [...jobTitlesList, ...calculatorParticipants.map((participant) => ({ title: participant.name }))], [jobTitlesList, calculatorParticipants]);

  const posCategoriesSelectorOptions = useMemo(
    () => [...sourceOptions, ...mapCategories(initialValues.categoryGroups || [], ShareRuleType.CATEGORY_GROUP), ...mapCategories(posCategories || [], ShareRuleType.CATEGORY)],
    [posCategories, initialValues]
  );
  const posEmployeesSelectorOptions = useMemo(() => mapEmployees(posEmployees || [], initialValues.rules, selectedRule), [posEmployees, initialValues, selectedRule]);

  const [showCancelModal, hideCancelModal] = useModal(
    () => (
      <ModalFactory customModal onClose={hideCancelModal} isActive>
        <AlertContent
          title={`Abandon ${toTitleCase(contributingJobTitle)} Share?`}
          message="Please note that your changes will not be saved"
          leftActionLabel="Cancel"
          rightActionLabel="Confirm"
          onClose={hideCancelModal}
          onLeftAction={hideCancelModal}
          onRightAction={() => {
            hideModal();
            hideCancelModal();
          }}
          rightActionAppearance="destructive"
        />
      </ModalFactory>
    ),
    [hideModal, contributingJobTitle]
  );

  const handleCancel = (values: ITipShareFormValues, dirty: boolean) => {
    if (dirty) {
      setContributingJobTitle(values.contributor?.name ?? '');
      showCancelModal();
    } else {
      hideModal();
    }
  };

  const validateVirtualRule = (values: ITipShareFormValues, createError: (params?: Yup.CreateErrorOptions) => Yup.ValidationError) => {
    const contributingJob = values.contributor?.name?.trim().toLowerCase();
    if (contributingJob) {
      const isJobRule = values.contributor.type === CalculatorParticipantType.JOB;

      // contributor cannot be virtual if it is benefiting from remainder
      if (values.sharePolicy?.remainder.beneficiaries?.some((b) => b.participants.some((p) => p.name?.trim().toLowerCase() === contributingJob))) {
        return createError({ message: `This ${isJobRule ? 'job' : 'owner'} is receiving tips below, so it can't be virtual.` });
      }

      // contributor cannot be virtual if it is benefiting from another's rule policy
      if (
        initialValues.rules?.some(
          (r) =>
            r.id !== selectedRule?.id &&
            r.sharePolicy &&
            (r.sharePolicy.remainder.beneficiaries?.some((b) => b.participants.some((p) => p.name.trim().toLowerCase() === contributingJob)) ||
              r.sharePolicy.shares.some((s) => s.beneficiaries.some((b) => b.name.trim().toLowerCase() === contributingJob)))
        )
      ) {
        return createError({ message: `This ${isJobRule ? 'job' : 'owner'} is receiving tips from another tip share, so it can't be virtual.` });
      }
    }
    return true;
  };

  const validationSchema = Yup.object().shape({
    contributor: Yup.object()
      .shape({
        name: Yup.string().required('Please select a contributor'),
        type: Yup.string(),
        providerId: Yup.string(),
      })
      .when('isVirtual', {
        is: true,
        then: (schema) => schema.test('isValidVirtual', 'Error - Invalid virtual', (_, ctx) => validateVirtualRule(ctx.parent, ctx.createError)),
      }),
    value: Yup.number(),
    isVirtual: Yup.boolean(),
    sharePolicy: Yup.object().shape({
      remainder: Yup.object().shape({
        type: Yup.string().required('Please select remainder'),
        beneficiaries: Yup.array().when('type', {
          is: ShareRemainderType.POOL,
          then: () =>
            Yup.array().of(
              Yup.object().shape({
                participants: Yup.array().of(
                  Yup.object().shape({
                    name: Yup.string().required('Please select a benefiting job'),
                  })
                ),
                split: Yup.string().required('Required'),
                source: Yup.string().required('Required'),
                condition: Yup.object().shape({
                  type: Yup.string(),
                  hours: Yup.number()
                    .test('isValidHours', 'Error - Hour allocation must be between 0.1 - 100', (value) => validatePercentage(Number(value)))
                    .when('type', { is: CalculatorRuleCondition.LABOR_CAP, then: (schema) => schema.required('Required') }),
                }),
              })
            ),
        }),
      }),
      shares: Yup.array().of(
        Yup.object().shape({
          share: Yup.number()
            .required('Required')
            .test('isValidShare', 'Error - Contribution must be between 0.1% - 100%', (value) => validatePercentage(Number(value))),
          eligibilityMethod: Yup.string().required('Required'),
          beneficiaries: Yup.array().of(
            Yup.object().shape({
              name: Yup.string()
                .required('Please select a benefiting job')
                .test('isValidShareJob', 'Error - A job cannot benefit from the same source more than once', (value, ctx) => validateShareJob(value, ctx.options)),
              split: Yup.string().required('Required'),
            })
          ),
          category: Yup.string().required('Required'),
        })
      ),
    }),
  });

  const onAddRule = (arrayHelpers: FieldArrayRenderProps) => {
    const id = uuid();
    arrayHelpers.push({
      id,
      beneficiaries: [{ ...defaultShare, id }],
      share: '',
      eligibilityMethod: ShareEligibilityMethod.BY_TICKET,
    });
    requestAnimationFrame(() => {
      document.getElementById(`tip-percentage-input-${id}`).focus();
    });
  };

  const onAddRemainder = (arrayHelpers: FieldArrayRenderProps) => {
    const id = uuid();
    arrayHelpers.push(defaultRemainder(id));
    requestAnimationFrame(() => {
      document.getElementById(`remainder-job-title-selector-${id}`).focus();
    });
  };

  const onAddJob = (arrayHelpers: FieldArrayRenderProps, element: 'share' | 'remainder') => {
    const id = uuid();
    arrayHelpers.push({ ...(element === 'share' ? defaultShare : defaultJob), id });
    requestAnimationFrame(() => {
      document.getElementById(`${element}-job-title-selector-${id}`).focus();
    });
  };

  const handleRemoveShareJob = (jobsArrayHelpers: FieldArrayRenderProps, jobsIndex: number, sharesArrayHelpers: FieldArrayRenderProps, sharesIndex: number, values: ITipShareFormValues) => {
    const jobsArray = values.sharePolicy?.shares[sharesIndex].beneficiaries;

    if (jobsArray.length > 1) {
      jobsArrayHelpers.remove(jobsIndex);
    } else {
      sharesArrayHelpers.remove(sharesIndex);
    }
  };

  const handleRemoveRemainderJob = (remainderArrayHelpers: FieldArrayRenderProps, rIndex: number, participantsArrayHelpers: FieldArrayRenderProps, pIndex: number, values: ITipShareFormValues) => {
    const beneficiaries = values.sharePolicy?.remainder?.beneficiaries;
    if (!beneficiaries || !beneficiaries[rIndex]) return;

    const { participants } = beneficiaries[rIndex];

    if (participants.length > 1) {
      participantsArrayHelpers.remove(pIndex);
    } else {
      remainderArrayHelpers.remove(rIndex);
    }
  };

  const initialFormValues: ITipShareFormValues = useMemo(() => {
    const id = uuid();
    const remainderBeneficiaries = selectedRule?.sharePolicy?.remainder.beneficiaries?.map((beneficiary, index) => ({
      ...beneficiary,
      id: index + id,
      source: orderBy(beneficiary.sources).join('|'),
      condition: beneficiary.conditions?.[0],
      participants: beneficiary.participants.map((participant, index) => ({ id: id + index, ...participant })),
    }));
    return {
      id: selectedRule?.id || id,
      value: 1,
      contributor: selectedRule?.contributor || { name: undefined, type: CalculatorParticipantType.JOB },
      isVirtual: selectedRule?.isVirtual || false,
      sharePolicy: {
        remainder: {
          type: selectedRule?.sharePolicy?.remainder.type,
          beneficiaries: remainderBeneficiaries?.length > 0 ? remainderBeneficiaries : [defaultRemainder(id)],
        },
        shares:
          selectedRule?.sharePolicy?.shares?.map((shareItem, index) => ({
            ...shareItem,
            id: id + index,
            beneficiaries: shareItem.beneficiaries?.map((beneficiary, index) => ({ id: index + id, ...beneficiary })),
            share: roundNumber(shareItem.share * 100, 2),
            category: shareItem.type === ShareRuleType.TIP ? orderBy(shareItem.sources).join('|') : shareItem.category,
          })) || [],
      },
    };
  }, [selectedRule]);

  const filterContributingJobs = (values: ITipShareFormValues) => {
    const jobs: string[] = [];

    // filter jobs from other rules with share policy
    initialValues.rules.forEach((rule) => {
      if (rule.sharePolicy && rule.id !== selectedRule?.id && rule.contributor?.name && rule.contributor.type === CalculatorParticipantType.JOB) {
        jobs.push(rule.contributor.name);
        if (rule.additionalContributors?.length) jobs.push(...rule.additionalContributors.map((j) => j.name));
      }
    });

    // filter jobs already benefiting from current share policy
    values.sharePolicy?.shares?.forEach((share) => {
      share?.beneficiaries?.forEach((job) => {
        if (job?.name) jobs.push(job.name);
      });
    });

    // filter defined participants like "House Account"
    calculatorParticipants.forEach((participant) => {
      jobs.push(participant.name);
    });

    return jobs;
  };

  const filterBenefitingJobs = (values: ITipShareFormValues, currentContributingJob?: string, currentBenefitingJob?: string, currentShare?: IFormCalculatorRuleShare, isRemainder?: boolean) => {
    const jobs: string[] = [];
    if (currentContributingJob) jobs.push(currentContributingJob);

    if (isRemainder) {
      // filter jobs already benefiting from remainder
      values.sharePolicy?.remainder.beneficiaries?.forEach((beneficiary) =>
        beneficiary.participants.forEach((participant) => {
          if (participant.name && participant.name !== currentBenefitingJob) jobs.push(participant.name);
        })
      );
    } else if (currentShare.category) {
      // filter jobs already benefiting from current sales category
      values.sharePolicy?.shares?.forEach((share) => {
        if (checkCategoryMatch(share, currentShare)) {
          share?.beneficiaries?.forEach((beneficiary) => {
            if (beneficiary?.name && beneficiary.name !== currentBenefitingJob) jobs.push(beneficiary.name);
          });
        }
      });
    }

    // filter shared terminals that cannot benefit from share policies
    initialValues.rules?.forEach((rule) => {
      if (rule.isVirtual && rule.id !== selectedRule?.id && rule.contributor?.name) {
        jobs.push(rule.contributor.name);
        if (rule.additionalContributors?.length) jobs.push(...rule.additionalContributors.map((j) => j.name));
      }
    });

    if (isRemainder || currentShare.beneficiaries.length > 1) {
      // filter defined participants like "House Account"
      calculatorParticipants.forEach((participant) => {
        jobs.push(participant.name);
      });
    }
    return jobs;
  };

  const handleSubmit = (values: ITipShareFormValues) => {
    let existingRules = initialValues.rules;

    const currentJobTitle = values.contributor.name.toLowerCase();
    let existingJobRule = initialValues.rules.find((existingRule) => existingRule.contributor?.name.toLowerCase() === currentJobTitle);

    if (selectedRule) {
      if (existingJobRule && selectedRule.id !== existingJobRule.id) {
        // user changed job title of selected rule to the title of another rule, so we remove the original
        existingRules = existingRules.filter((rule) => rule.id !== selectedRule.id);
      } else {
        existingJobRule = selectedRule;
      }
    }

    const newRule = {
      ...values,
      value: existingJobRule ? existingJobRule.value : values.value,
      sharePolicy: {
        ...values.sharePolicy,
        remainder: {
          type: values.isVirtual ? ShareRemainderType.POOL : values.sharePolicy.remainder.type,
          beneficiaries:
            values.sharePolicy.remainder.type === ShareRemainderType.SELF
              ? []
              : values.sharePolicy.remainder.beneficiaries.map((beneficiary) => ({
                  participants: beneficiary.participants,
                  split: beneficiary.split,
                  sources: beneficiary.source?.split('|') as RuleFundSource[],
                  conditions: beneficiary.condition?.type === CalculatorRuleCondition.LABOR_CAP ? [{ ...beneficiary.condition, hours: Number(beneficiary.condition.hours) }] : [],
                })),
        },
        shares: values.sharePolicy.shares.map((shareContent) => ({
          beneficiaries: shareContent.beneficiaries,
          share: roundNumber(Number(shareContent.share) / 100, 4),
          eligibilityMethod: shareContent.eligibilityMethod,
          type: shareContent.type,
          ...(shareContent.type === ShareRuleType.TIP ? { sources: shareContent.category.split('|') as RuleFundSource[] } : { category: shareContent.category, sources: [RuleFundSource.AUTO_GRATUITIES, RuleFundSource.TIPS] }),
        })),
      },
    };

    const mapNewRules = () => existingRules.map((rule) => (rule?.id === existingJobRule.id ? newRule : rule));

    const valuesToBeSubmitted: IFormValues = {
      ...initialValues,
      rules: existingJobRule ? mapNewRules() : [...existingRules, newRule],
      participants: calculatorParticipants,
    };

    onConfirm(valuesToBeSubmitted, hideModal);
  };

  const getSharesError = (sharesErrors: string | string[] | FormikErrors<IFormCalculatorRuleShare>[], touched: FormikTouched<IFormCalculatorRuleShare>[]) => {
    let errorMsg = '';

    // no error is shown if elements have not been touched
    if (!touched?.some((share) => share)) return errorMsg;

    if (typeof sharesErrors === 'string') {
      errorMsg = sharesErrors;
    } else if (sharesErrors?.length > 0) {
      (sharesErrors as FormikErrors<IFormCalculatorRuleShare>[]).some((share, i) => {
        if (touched[i]?.share && typeof share?.share === 'string' && share.share !== 'Required') {
          errorMsg = share.share;
        } else if (share?.beneficiaries?.length && touched[i]?.beneficiaries) {
          return (share.beneficiaries as FormikErrors<IFormBeneficiary>[]).some((jobError, j) => {
            if (touched[i].beneficiaries[j]?.name) errorMsg = jobError?.name;
            return errorMsg;
          });
        }
        return errorMsg;
      });
    }
    return errorMsg;
  };

  const getRemainderError = (remainderErrors: string | string[] | FormikErrors<IFormRemainderBeneficiary>[], values: IFormRemainderBeneficiary[]) => {
    let errorMsg = '';

    if (typeof remainderErrors === 'string') {
      errorMsg = remainderErrors;
    } else if (remainderErrors?.length > 0) {
      (remainderErrors as FormikErrors<IFormRemainderBeneficiary>[]).some((jobError, i) => {
        if (values[i]?.condition?.hours) errorMsg = jobError?.condition?.hours;
        return errorMsg;
      });
    }
    return errorMsg;
  };

  return (
    <ModalFactory title={`${selectedRule ? 'Edit' : 'Create'} Tip Share`} onClose={showCancelModal} isActive maxWidth={1000} closeOnOutSideClick={false} hideCloseButton>
      <ModalContent>
        <ModalInfo size="small" padding={130}>
          Each tip share includes one contributing Job or Owner and one or more benefiting jobs. To add another contributing role, create another tip share after saving.
        </ModalInfo>
        <Formik initialValues={initialFormValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
          {({ setFieldValue, setValues, values, isValid, dirty, setFieldTouched, errors, touched }) => {
            const sharesError = getSharesError(errors?.sharePolicy?.shares, touched?.sharePolicy?.shares);
            const remainderError = getRemainderError(errors?.sharePolicy?.remainder?.beneficiaries, values?.sharePolicy?.remainder?.beneficiaries);
            const isJobRule = values.contributor.type === CalculatorParticipantType.JOB;
            const onContributorTypeChange = () => {
              const newType = isJobRule ? CalculatorParticipantType.EMPLOYEE : CalculatorParticipantType.JOB;
              setFieldTouched('contributor', false, false); // reset field
              setFieldValue('contributor', selectedRule?.contributor?.type === newType ? selectedRule.contributor : { name: undefined, type: newType });
            };
            return (
              <Form>
                <TipShareFormContainer>
                  <Column gap={12}>
                    <SectionTitle>Select Contributing</SectionTitle>
                    <ContributingCardsRow role="radiogroup">
                      <CardRadio title="Job Rule" name="jobRule" description={jobRuleText} checked={isJobRule} onChange={onContributorTypeChange} dataCy="job-rule-card" />
                      <CardRadio title="Owner Rule" name="ownerRule" description={ownerRuleText} checked={!isJobRule} onChange={onContributorTypeChange} dataCy="owner-rule-card" />
                    </ContributingCardsRow>
                  </Column>

                  <FieldContainer>
                    <Field name="isVirtual">
                      {({ field }: FieldProps) => (
                        <StyledCheckbox
                          {...field}
                          onChange={() => {
                            const isVirtual = !field.value;
                            const hasRemainder = !!values.sharePolicy.remainder.beneficiaries[0].participants[0].name;
                            setValues({
                              ...values,
                              isVirtual,
                              sharePolicy: {
                                ...values.sharePolicy,
                                remainder: {
                                  ...values.sharePolicy.remainder,
                                  type: isVirtual || hasRemainder ? ShareRemainderType.POOL : undefined,
                                },
                              },
                            });
                          }}
                          data-cy="virtual-checkbox"
                          label="Virtual"
                          size="small"
                          id="virtual-checkbox"
                          checked={field.value}
                        />
                      )}
                    </Field>
                    <TipSharesText size="xsmall">Check this if the {isJobRule ? 'job' : 'owner'} is used by an automated system or shared terminal.</TipSharesText>
                  </FieldContainer>

                  <Column gap={8}>
                    <SectionTitle>Select {displayContributorType(values)}</SectionTitle>
                    <Field name="contributor">
                      {({ field, meta }: FieldProps) => (
                        <Column gap={0}>
                          {isJobRule ? (
                            <JobTitleField
                              {...field}
                              defaultValue={field.value?.name}
                              placeholder="Select job"
                              siteId={siteId}
                              onChange={(job) => {
                                if (job) {
                                  if (!meta.touched) setFieldTouched('contributor');
                                  setFieldValue('contributor.name', job.value.title);
                                }
                              }}
                              filterOptions={filterContributingJobs(values)}
                              $error={meta.error && field.value?.name}
                              loadOptions={false}
                              customOptions={participantsSelectorOptions}
                              onNewJobTitle={onNewJobTitle}
                              dataCy="contributing-job-title-selector"
                              selectId="contributing-job-title-selector"
                            />
                          ) : (
                            <Select
                              {...field}
                              data-cy="owner-selector"
                              values={field.value?.providerId ? [{ value: field.value.providerId, label: toTitleCase(field.value.name) }] : []}
                              options={posEmployeesSelectorOptions}
                              onChange={(selected: ISelectOption<string>[]) => {
                                if (!meta.touched) setFieldTouched('contributor');
                                setFieldValue('contributor', { ...field.value, name: selected[0]?.label, providerId: selected[0]?.value });
                              }}
                              error={meta.error && field.value?.name}
                              placeholder="Select owner"
                              selectId="owner-selector"
                              className="selector"
                              searchable
                            />
                          )}
                          {field.value?.name && meta.error && <ErrorText size="xxsmall">{typeof meta.error === 'string' ? meta.error : (meta.error as ICalculatorParticipant).name}</ErrorText>}
                        </Column>
                      )}
                    </Field>
                  </Column>

                  <Column gap={16}>
                    <Column gap={8}>
                      <SectionTitle>Sharing Rules</SectionTitle>
                      {sharesError && (
                        <ErrorText size="xxsmall" data-cy="tip-share-error">
                          {sharesError}
                        </ErrorText>
                      )}
                    </Column>
                    <FieldArray
                      name="sharePolicy.shares"
                      render={(sharesArrayHelpers) => (
                        <TipCalculatorFieldsContainer>
                          {values?.sharePolicy?.shares?.map((share, sharesIndex) => (
                            <FieldArray
                              key={`share_${share.id}`}
                              name={`sharePolicy.shares.${sharesIndex}.beneficiaries`}
                              render={(jobsArrayHelpers) => (
                                <TipShareRow key={share.id} data-cy="tip-share-rule-row">
                                  <FieldContainer className="shares-container">
                                    <StyledRow>
                                      <TipSharesText size="xsmall">Shares</TipSharesText>
                                      <StyledButtonText label="Remove Rule" appearance="destructive" hideDesktop onClick={() => sharesArrayHelpers.remove(sharesIndex)} />
                                    </StyledRow>
                                    <Field name={`sharePolicy.shares.${sharesIndex}.share`}>
                                      {({ field, meta }: FieldProps) => (
                                        <InputIconWrapper icon="Percentage" iconPosition="right">
                                          <NumberInput
                                            {...field}
                                            inputMode="decimal"
                                            onChange={(e) => onPointsChange(e, field.onChange)}
                                            value={field.value || ''}
                                            $error={meta.error && meta.touched}
                                            data-cy="tip-percentage-input"
                                            id={`tip-percentage-input-${share.id}`}
                                          />
                                        </InputIconWrapper>
                                      )}
                                    </Field>
                                  </FieldContainer>

                                  <FieldContainer className="source-container">
                                    <TipSharesText size="xsmall">of</TipSharesText>

                                    <TipCalculatorRow>
                                      <Field name={`sharePolicy.shares.${sharesIndex}.category`} className="selector">
                                        {({ field, meta }: FieldProps) => (
                                          <Select
                                            {...field}
                                            data-cy="pos-category-selector"
                                            values={field.value ? [posCategoriesSelectorOptions.find((opt) => opt.value.category === field.value) || { value: field.value, label: toTitleCase(field.value) }] : []}
                                            options={posCategoriesSelectorOptions}
                                            sections={sections}
                                            onChange={(selected: ISelectOption<any>[]) => {
                                              if (!meta.touched) setFieldTouched(`sharePolicy.shares.${sharesIndex}.category`);
                                              setFieldValue(`sharePolicy.shares.${sharesIndex}`, { ...values.sharePolicy.shares[sharesIndex], ...selected[0].value });
                                            }}
                                            valueField="value.category"
                                            error={meta.error && meta.touched}
                                            placeholder="Select source"
                                            id="pos-category-selector"
                                            className="selector"
                                            searchable
                                          />
                                        )}
                                      </Field>

                                      <Field name={`sharePolicy.shares.${sharesIndex}.eligibilityMethod`}>
                                        {({ field }: FieldProps) => (
                                          <InputIcons
                                            options={eligibilityMethodOptions}
                                            value={field.value}
                                            onIconClick={(value) => setFieldValue(`sharePolicy.shares.${sharesIndex}.eligibilityMethod`, value)}
                                            disabled={share.beneficiaries[0].type === CalculatorParticipantType.HOUSE}
                                            width={60}
                                            id={`eligibility_${sharesIndex}`}
                                            data-cy="eligibility-input"
                                          />
                                        )}
                                      </Field>
                                    </TipCalculatorRow>
                                  </FieldContainer>

                                  <TipCalculatorFieldsContainer fullWidth>
                                    {share.beneficiaries?.map((item, jobsIndex) => (
                                      <FieldContainer key={`job_${item.id}`}>
                                        <TipSharesText size="xsmall" hideMobile={jobsIndex > 0}>
                                          {jobsIndex > 0 ? '&' : 'to'}
                                        </TipSharesText>

                                        <TipCalculatorRow>
                                          <Field name={`sharePolicy.shares.${sharesIndex}.beneficiaries.${jobsIndex}`}>
                                            {({ field, meta }: FieldProps) => (
                                              <JobTitleField
                                                {...field}
                                                defaultValue={field.value?.name}
                                                placeholder="Select job"
                                                siteId={siteId}
                                                onChange={(job) => {
                                                  if (job) {
                                                    if (!meta.touched) setFieldTouched(`sharePolicy.shares.${sharesIndex}.beneficiaries.${jobsIndex}.name`);
                                                    if (job.value?.title === HOUSE_ACCOUNT_NAME) {
                                                      setFieldValue(`sharePolicy.shares.${sharesIndex}`, {
                                                        ...share,
                                                        beneficiaries: [{ ...field.value, name: job.value?.title, type: CalculatorParticipantType.HOUSE, split: CalculatorSplitMethod.NONE }],
                                                        eligibilityMethod: ShareEligibilityMethod.BY_SEGMENT,
                                                      });
                                                    } else {
                                                      setFieldValue(`sharePolicy.shares.${sharesIndex}.beneficiaries.${jobsIndex}`, {
                                                        ...field.value,
                                                        name: job.value?.title,
                                                        type: CalculatorParticipantType.JOB,
                                                        split: field.value?.split === CalculatorSplitMethod.NONE ? CalculatorSplitMethod.TIME_WORKED : field.value?.split,
                                                      });
                                                    }
                                                  }
                                                }}
                                                filterOptions={filterBenefitingJobs(values, values.contributor?.name, field.value?.name, share)}
                                                $error={meta.error && field.value?.name}
                                                loadOptions={false}
                                                customOptions={participantsSelectorOptions}
                                                onNewJobTitle={onNewJobTitle}
                                                dataCy="share-job-title-selector"
                                                selectId={`share-job-title-selector-${item.id}`}
                                                className="selector"
                                              />
                                            )}
                                          </Field>

                                          <Field name={`sharePolicy.shares.${sharesIndex}.beneficiaries.${jobsIndex}.split`}>
                                            {({ field }: FieldProps) => (
                                              <InputIcons
                                                options={jobSplitOptions}
                                                value={field.value}
                                                onIconClick={(value) => setFieldValue(`sharePolicy.shares.${sharesIndex}.beneficiaries.${jobsIndex}.split`, value)}
                                                disabled={share.beneficiaries[0].type === CalculatorParticipantType.HOUSE}
                                                width={60}
                                                id={`split_${jobsIndex}`}
                                                data-cy="split-input"
                                              />
                                            )}
                                          </Field>

                                          <StyledIconButton
                                            label="Add Job"
                                            onClick={() => onAddJob(jobsArrayHelpers, 'share')}
                                            disabled={share.beneficiaries[0].type === CalculatorParticipantType.HOUSE}
                                            appearance="white"
                                            size="large"
                                            icon="Plus"
                                            data-cy="add-share-job-button"
                                            $noMargin
                                            $hideMobile
                                          />
                                          <StyledIconButton
                                            label="Remove"
                                            onClick={() => handleRemoveShareJob(jobsArrayHelpers, jobsIndex, sharesArrayHelpers, sharesIndex, values)}
                                            appearance="white"
                                            size="small"
                                            icon="Close"
                                            data-cy="remove-share-job-button"
                                            $noMargin
                                            $hideMobile={share.beneficiaries.length === 1}
                                          />
                                        </TipCalculatorRow>
                                      </FieldContainer>
                                    ))}
                                  </TipCalculatorFieldsContainer>

                                  <StyledButtonText label="Add Job" hideDesktop onClick={() => onAddJob(jobsArrayHelpers, 'share')} />
                                </TipShareRow>
                              )}
                            />
                          ))}
                          <AddButton $tipShare appearance="secondary" icon="Plus" label="Add Rule" onClick={() => onAddRule(sharesArrayHelpers)} data-cy="add-rule-button" />
                        </TipCalculatorFieldsContainer>
                      )}
                    />
                  </Column>

                  <Column gap={8}>
                    <SectionTitle>Allocate Remainder To</SectionTitle>
                    {remainderError && (
                      <ErrorText size="xxsmall" data-cy="remainder-error">
                        {remainderError}
                      </ErrorText>
                    )}
                    {!values.isVirtual && (
                      <StyledDiv>
                        <Field name="sharePolicy.remainder.type">
                          {({ field }: FieldProps) => (
                            <Select
                              {...field}
                              data-cy="remainder-type-selector"
                              values={[remainderSelectorOptions.find((opt) => opt.value === field.value)]}
                              options={remainderSelectorOptions}
                              onChange={(selected: ISelectOption<string>[]) => setFieldValue('sharePolicy.remainder.type', selected[0].value)}
                              placeholder="Select"
                              id="remainder-type-selector"
                            />
                          )}
                        </Field>
                      </StyledDiv>
                    )}
                    {values.sharePolicy?.remainder.type === ShareRemainderType.POOL && (
                      <FieldArray
                        name="sharePolicy.remainder.beneficiaries"
                        render={(remainderArrayHelpers) => (
                          <TipCalculatorFieldsContainer>
                            {values.sharePolicy?.remainder.beneficiaries?.map((beneficiary, bIndex) => (
                              <TipShareRow key={`remainder_${beneficiary.id}`}>
                                <Column>
                                  <TipSharesText size="xsmall" hideDesktop={bIndex !== 0}>
                                    Source
                                  </TipSharesText>
                                  <Field name={`sharePolicy.remainder.beneficiaries.${bIndex}.source`}>
                                    {({ field, meta }: FieldProps) => (
                                      <Select
                                        {...field}
                                        values={field.value ? [sourceOptions.find((opt) => opt.value.category === field.value) || { value: field.value, label: toTitleCase(field.value) }] : []}
                                        options={sourceOptions}
                                        onChange={(selected: ISelectOption<any>[]) => {
                                          if (!meta.touched) setFieldTouched(`sharePolicy.remainder.beneficiaries.${bIndex}.source`);
                                          setFieldValue(`sharePolicy.remainder.beneficiaries.${bIndex}.source`, selected[0].value?.category);
                                        }}
                                        valueField="value.category"
                                        error={meta.error && meta.touched}
                                        placeholder="Select source"
                                        id="remainder-source-selector"
                                        data-cy="remainder-source-selector"
                                        className="selector"
                                        searchable
                                      />
                                    )}
                                  </Field>
                                </Column>

                                <TipCalculatorRow className="remainder-split-row">
                                  <Column className="split-container">
                                    <TipSharesText size="xsmall" hideDesktop={bIndex !== 0}>
                                      Split
                                    </TipSharesText>
                                    <Field name={`sharePolicy.remainder.beneficiaries.${bIndex}.split`}>
                                      {({ field }: FieldProps) => (
                                        <InputIcons
                                          options={jobSplitOptions}
                                          value={field.value}
                                          onIconClick={(value) => setFieldValue(`sharePolicy.remainder.beneficiaries.${bIndex}.split`, value)}
                                          width={60}
                                          id={`remainder_split_${bIndex}`}
                                          data-cy="remainder-split-input"
                                        />
                                      )}
                                    </Field>
                                  </Column>
                                  <Column>
                                    <TipSharesText size="xsmall" hideDesktop={bIndex !== 0}>
                                      Hour Allocation
                                    </TipSharesText>
                                    <Field name={`sharePolicy.remainder.beneficiaries.${bIndex}.condition`}>
                                      {({ field, meta }: FieldProps) => (
                                        <NumberInput
                                          {...field}
                                          inputMode="decimal"
                                          onChange={(e) =>
                                            onPointsChange(e, () => {
                                              setFieldValue(`sharePolicy.remainder.beneficiaries.${bIndex}.condition`, e.target.value === '' ? undefined : { type: CalculatorRuleCondition.LABOR_CAP, hours: e.target.value });
                                            })
                                          }
                                          value={field.value?.hours || ''}
                                          align="center"
                                          $error={meta.error && field.value?.hours}
                                          $width={100}
                                          data-cy="hour-allocation-input"
                                          id={`hour-allocation-input-${bIndex}`}
                                        />
                                      )}
                                    </Field>
                                  </Column>
                                </TipCalculatorRow>

                                <Column>
                                  <TipSharesText size="xsmall" hideDesktop={bIndex !== 0}>
                                    Job
                                  </TipSharesText>
                                  <FieldArray
                                    name={`sharePolicy.remainder.beneficiaries.${bIndex}.participants`}
                                    render={(participantsArrayHelpers) =>
                                      beneficiary.participants.map((participant, pIndex) => (
                                        <TipCalculatorRow key={`participant_${participant.id}`}>
                                          {pIndex !== 0 && (
                                            <TipSharesText size="xsmall" hideMobile>
                                              &
                                            </TipSharesText>
                                          )}
                                          <Field name={`sharePolicy.remainder.beneficiaries.${bIndex}.participants.${pIndex}`}>
                                            {({ field, meta }: FieldProps) => (
                                              <JobTitleField
                                                {...field}
                                                defaultValue={field.value?.name}
                                                placeholder="Select job"
                                                siteId={siteId}
                                                onChange={(job) => {
                                                  if (job) {
                                                    if (!meta.touched) setFieldTouched(`sharePolicy.remainder.beneficiaries.${bIndex}.participants.${pIndex}.name`);
                                                    setFieldValue(`sharePolicy.remainder.beneficiaries.${bIndex}.participants.${pIndex}.name`, job.value.title);
                                                  }
                                                }}
                                                filterOptions={filterBenefitingJobs(values, undefined, field.value?.name, undefined, true)}
                                                $error={field.value?.name && (!!meta.error || checkVirtualRuleConflict(values, field.value.name))}
                                                loadOptions={false}
                                                customOptions={participantsSelectorOptions}
                                                onNewJobTitle={onNewJobTitle}
                                                dataCy="remainder-job-title-selector"
                                                selectId={`remainder-job-title-selector-${participant.id}`}
                                                className="selector"
                                              />
                                            )}
                                          </Field>

                                          <StyledIconButton label="Add Job" onClick={() => onAddJob(participantsArrayHelpers, 'remainder')} appearance="white" size="large" icon="Plus" data-cy="add-remainder-job-button" $noMargin />

                                          {(values.sharePolicy.remainder.beneficiaries.length > 1 || beneficiary.participants.length > 1) && (
                                            <StyledIconButton
                                              label="Remove"
                                              onClick={() => handleRemoveRemainderJob(remainderArrayHelpers, bIndex, participantsArrayHelpers, pIndex, values)}
                                              appearance="white"
                                              size="small"
                                              icon="Close"
                                              data-cy="remove-remainder-job-button"
                                              $noMargin
                                              $hideMobile={beneficiary.participants.length === 1}
                                            />
                                          )}
                                        </TipCalculatorRow>
                                      ))
                                    }
                                  />
                                </Column>

                                {values.sharePolicy?.remainder.beneficiaries.length > 1 && <StyledButtonText label="Remove Pool" appearance="destructive" hideDesktop onClick={() => remainderArrayHelpers.remove(bIndex)} />}
                              </TipShareRow>
                            ))}
                            {values.sharePolicy.remainder.beneficiaries.length < appConfig.TIP_CALCULATOR.REMAINDER_JOBS_LIMIT && (
                              <AddButton $tipShare appearance="secondary" icon="Plus" label="Add Job" onClick={() => onAddRemainder(remainderArrayHelpers)} data-cy="add-remainder-button" />
                            )}
                          </TipCalculatorFieldsContainer>
                        )}
                      />
                    )}
                  </Column>
                </TipShareFormContainer>
                <ButtonContainer isModal>
                  <StyledButton label="Cancel" data-cy="cancel-button" appearance="white" onClick={() => handleCancel(values, dirty)} />
                  <StyledButton type="submit" label="Save" data-cy="save-rules-button" isProcessing={isLoading} disabled={isLoading || !dirty || !isValid} />
                </ButtonContainer>
              </Form>
            );
          }}
        </Formik>
      </ModalContent>
    </ModalFactory>
  );
};
